import type { CSSProperties } from 'react';
import { motionFrame, type CutStyle, type ElementMotion } from '@rehub/propertycut-timeline';
import type { RenderFormat } from '../../types';

/** Preview styles for an element `local` seconds into its `length` on screen. */
export function motionCss(motion: ElementMotion | undefined, local: number, length: number): CSSProperties {
  const f = motionFrame(motion, local, length);
  const style: CSSProperties = {};
  if (f.opacity !== 1) style.opacity = f.opacity;
  const parts: string[] = [];
  if (f.dx !== 0 || f.dy !== 0) parts.push(`translate(calc(${f.dx} * 100cqw), calc(${-f.dy} * 100cqh))`);
  if (f.rotate !== 0) parts.push(`rotate(${f.rotate}deg)`);
  if (f.scale !== 1) parts.push(`scale(${f.scale})`);
  if (parts.length) style.transform = parts.join(' ');
  if (f.reveal < 1) style.clipPath = `inset(0 ${(1 - f.reveal) * 100}% 0 0)`;
  return style;
}

export const CLIP_TRANSITIONS: Array<{ value: Exclude<CutStyle, 'crossfade'>; label: string; hint: string }> = [
  { value: 'cut', label: 'Cut', hint: 'Hard cut, no blend' },
  { value: 'dissolve', label: 'Dissolve', hint: 'Soft crossfade' },
  { value: 'wipe', label: 'Wipe', hint: 'Soft edge sweeps across' },
  { value: 'slide', label: 'Slide', hint: 'Pushes in from the right' },
  { value: 'zoom', label: 'Zoom', hint: 'Punches in over the previous clip' },
  { value: 'reveal', label: 'Reveal', hint: 'A hard edge uncovers the next clip' },
  { value: 'carousel', label: 'Carousel', hint: 'Slides up from below' },
];

export function shownTransition(style: CutStyle): Exclude<CutStyle, 'crossfade'> {
  return style === 'crossfade' ? 'dissolve' : style;
}

export type EditorSelection =
  | { type: 'clip'; id: string }
  | { type: 'text'; id: string }
  | { type: 'audio'; id: string }
  | { type: 'logo' }
  | null;

export function draftTitle(format: RenderFormat): string {
  const label = format === 'landscape' ? 'Landscape' : format === 'square' ? 'Square' : 'Portrait';
  const date = new Date().toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
  return `${label} video · ${date}`;
}

export function formatLabel(format: RenderFormat): string {
  if (format === 'landscape') return 'Landscape · 16:9';
  if (format === 'vertical') return 'Portrait · 9:16';
  return 'Square · 1:1';
}

export function formatClock(seconds: number): string {
  const safe = Math.max(0, Number.isFinite(seconds) ? seconds : 0);
  const mins = Math.floor(safe / 60);
  const secs = safe - mins * 60;
  return `${mins}:${secs.toFixed(1).padStart(4, '0')}`;
}

export function classifyUpload(file: File): 'photo' | 'video' | 'audio' | null {
  if (file.type.startsWith('video/')) return 'video';
  if (file.type.startsWith('audio/')) return 'audio';
  if (file.type.startsWith('image/')) return 'photo';
  const name = file.name.toLowerCase();
  if (/\.(mp4|mov|m4v|webm)$/.test(name)) return 'video';
  if (/\.(mp3|wav|m4a|aac|ogg)$/.test(name)) return 'audio';
  if (/\.(jpe?g|png|webp|gif)$/.test(name)) return 'photo';
  return null;
}

export function cropStyle(offsetX: number, offsetY: number): string {
  const x = ((offsetX + 1) / 2) * 100;
  const y = ((1 - offsetY) / 2) * 100;
  return `${x}% ${y}%`;
}
